import React, { useEffect, useRef } from 'react';

// Debounce utility
function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

interface Star {
  x: number;
  y: number;
  size: number;
  baseOpacity: number;
  opacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
  vx: number;
  vy: number;
}

interface Comet {
  x: number;
  y: number;
  length: number;
  speed: number;
  angle: number;
  opacity: number;
  life: number;
  maxLife: number;
}

const STAR_COUNT = 50;
const COMET_INTERVAL_MIN = 5000;
const COMET_INTERVAL_MAX = 10000;

const CosmicBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let lastTime = 0;
    let nextCometTime = 0;

    canvas.width = width;
    canvas.height = height;

    // Create stars
    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 2.5 + 0.5,
        baseOpacity: Math.random() * 0.6 + 0.3,
        opacity: 0,
        twinkleSpeed: Math.random() * 2 + 1, // 1-3 seconds cycle
        twinklePhase: Math.random() * Math.PI * 2,
        vx: (Math.random() - 0.5) * 0.15, // very slow drift
        vy: (Math.random() - 0.5) * 0.15,
      });
    }

    // Active comets
    const comets: Comet[] = [];

    const spawnComet = () => {
      // Random start from any edge
      const edge = Math.floor(Math.random() * 4);
      let x: number, y: number, angle: number;

      switch (edge) {
        case 0: // top-left area
          x = -50;
          y = Math.random() * height * 0.5;
          angle = Math.PI / 6 + Math.random() * Math.PI / 4;
          break;
        case 1: // top-right area
          x = width + 50;
          y = Math.random() * height * 0.5;
          angle = Math.PI * 0.75 + Math.random() * Math.PI / 4;
          break;
        case 2: // left-bottom area
          x = Math.random() * width * 0.3;
          y = -50;
          angle = Math.PI / 4 + Math.random() * Math.PI / 3;
          break;
        default: // right-bottom area
          x = width + 50;
          y = height * 0.3 + Math.random() * height * 0.4;
          angle = Math.PI * 0.6 + Math.random() * Math.PI / 3;
          break;
      }

      comets.push({
        x,
        y,
        length: Math.random() * 80 + 60,
        speed: Math.random() * 4 + 3,
        angle,
        opacity: 0,
        life: 0,
        maxLife: Math.random() * 2000 + 1500,
      });
    };

    // Draw nebula background
    const drawNebula = () => {
      const gradient = ctx.createRadialGradient(
        width * 0.7, height * 0.4, 0,
        width * 0.7, height * 0.4, width * 0.5
      );
      gradient.addColorStop(0, 'rgba(123, 45, 180, 0.15)');
      gradient.addColorStop(0.5, 'rgba(80, 20, 150, 0.1)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      const gradient2 = ctx.createRadialGradient(
        width * 0.3, height * 0.6, 0,
        width * 0.3, height * 0.6, width * 0.4
      );
      gradient2.addColorStop(0, 'rgba(60, 20, 120, 0.12)');
      gradient2.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient2;
      ctx.fillRect(0, 0, width, height);
    };

    const drawStar = (star: Star, time: number) => {
      // Twinkle using sine wave
      const twinkle = Math.sin(time * star.twinkleSpeed + star.twinklePhase);
      star.opacity = star.baseOpacity * (0.5 + 0.5 * twinkle);

      // Move slowly
      star.x += star.vx;
      star.y += star.vy;

      // Wrap around edges
      if (star.x < -10) star.x = width + 10;
      if (star.x > width + 10) star.x = -10;
      if (star.y < -10) star.y = height + 10;
      if (star.y > height + 10) star.y = -10;

      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);

      // Slight yellow tint for some stars
      const warm = star.size > 1.8;
      if (warm) {
        ctx.fillStyle = `rgba(255, 248, 200, ${star.opacity})`;
      } else {
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
      }
      ctx.fill();

      // Glow for larger stars
      if (star.size > 1.5) {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * 0.08})`;
        ctx.fill();
      }
    };

    const drawComet = (comet: Comet) => {
      const dx = Math.cos(comet.angle) * comet.speed;
      const dy = Math.sin(comet.angle) * comet.speed;

      comet.x += dx;
      comet.y += dy;
      comet.life += 16; // approximate ms per frame

      // Fade in/out lifecycle
      const lifeRatio = comet.life / comet.maxLife;
      if (lifeRatio < 0.1) {
        comet.opacity = lifeRatio / 0.1;
      } else if (lifeRatio > 0.85) {
        comet.opacity = (1 - lifeRatio) / 0.15;
      } else {
        comet.opacity = 1;
      }

      if (comet.opacity <= 0 || comet.life > comet.maxLife) return;

      // Tail direction (opposite to movement)
      const tailX = -Math.cos(comet.angle);
      const tailY = -Math.sin(comet.angle);

      // Draw tail gradient
      const tailLen = comet.length * comet.opacity;
      const gradient = ctx.createLinearGradient(
        comet.x, comet.y,
        comet.x + tailX * tailLen,
        comet.y + tailY * tailLen
      );
      gradient.addColorStop(0, `rgba(180, 220, 255, ${0.9 * comet.opacity})`);
      gradient.addColorStop(0.3, `rgba(120, 200, 255, ${0.5 * comet.opacity})`);
      gradient.addColorStop(1, 'rgba(100, 180, 255, 0)');

      ctx.beginPath();
      ctx.moveTo(comet.x, comet.y);
      ctx.lineTo(
        comet.x + tailX * tailLen + tailY * 2,
        comet.y + tailY * tailLen - tailX * 2
      );
      ctx.lineTo(
        comet.x + tailX * tailLen - tailY * 2,
        comet.y + tailY * tailLen + tailX * 2
      );
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Head (bright white/cyan point)
      ctx.beginPath();
      ctx.arc(comet.x, comet.y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(220, 240, 255, ${comet.opacity})`;
      ctx.fill();

      // Head glow
      ctx.beginPath();
      ctx.arc(comet.x, comet.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(150, 210, 255, ${0.3 * comet.opacity})`;
      ctx.fill();
    };

    const animate = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp;
      lastTime = timestamp;

      const timeSec = timestamp / 1000;

      // Clear and draw base background
      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(0, 0, width, height);
      drawNebula();

      // Draw stars
      for (const star of stars) {
        drawStar(star, timeSec);
      }

      // Spawn comets on schedule
      if (timestamp > nextCometTime) {
        spawnComet();
        nextCometTime = timestamp + COMET_INTERVAL_MIN + Math.random() * (COMET_INTERVAL_MAX - COMET_INTERVAL_MIN);
      }

      // Update and draw comets
      for (let i = comets.length - 1; i >= 0; i--) {
        const comet = comets[i];
        drawComet(comet);
        if (comet.life > comet.maxLife || comet.opacity <= 0) {
          comets.splice(i, 1);
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    // Page Visibility API — pause animation when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(animationId);
      } else {
        lastTime = 0;
        animationId = requestAnimationFrame(animate);
      }
    };

    const handleResize = debounce(() => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    }, 150);

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default CosmicBackground;
